import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../App';
import { cleanSubmissionAnswers } from '@/utils/cleanAnswer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Eye, 
  Save, 
  Send,
  X,
  MessageSquare,
  Mic,
  Edit,
  Cloud,
  CloudOff,
  Loader2
} from 'lucide-react';
import axios from 'axios';

const SubmissionMarkingPage = () => {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [marks, setMarks] = useState({
    task1_ta: 0,
    task1_cc: 0,
    task1_lr: 0,
    task1_gra: 0,
    task2_ta: 0,
    task2_cc: 0,
    task2_lr: 0,
    task2_gra: 0,
    overall_summary: '',  // Legacy field
    summary_task1: '',    // Task 1 specific summary
    summary_task2: ''     // Task 2 specific summary
  });
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedText, setSelectedText] = useState(null);
  const [feedbackPopup, setFeedbackPopup] = useState({ show: false, x: 0, y: 0 });
  const [feedbackText, setFeedbackText] = useState('');
  const [inlineComments, setInlineComments] = useState([]);
  const [activeTask, setActiveTask] = useState(1);
  const answerRef = useRef(null);
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Auto-save states
  const [autoSaveStatus, setAutoSaveStatus] = useState('saved'); // 'saved', 'saving', 'unsaved', 'error'
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const autoSaveTimeoutRef = useRef(null);
  const hasUnsavedChanges = useRef(false);
  
  // Voice recording states - task specific
  const [isRecording, setIsRecording] = useState(false);
  const [voiceNote, setVoiceNote] = useState(null);  // Legacy
  const [voiceNoteTask1, setVoiceNoteTask1] = useState(null);
  const [voiceNoteTask2, setVoiceNoteTask2] = useState(null);
  const [audioURL, setAudioURL] = useState(null);
  const [audioURLTask1, setAudioURLTask1] = useState(null);
  const [audioURLTask2, setAudioURLTask2] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  useEffect(() => {
    fetchSubmissionDetails();
  }, [submissionId]);

  const fetchSubmissionDetails = async () => {
    try {
      const response = await axios.get(`/instructor/submissions/${submissionId}`);
      response.data = cleanSubmissionAnswers(response.data);
      setSubmission(response.data);
      
      // Set active task based on configuration
      if (response.data.test_details?.task_configuration === 'task_2_only') {
        setActiveTask(2);
      } else {
        setActiveTask(1);
      }
      
      // Load existing marks if any
      if (response.data.marks) {
        // Transform backend marks format to frontend format
        const backendMarks = response.data.marks;
        const transformedMarks = {
          task1_ta: backendMarks.task1_scores?.task_achievement || 0,
          task1_cc: backendMarks.task1_scores?.coherence_cohesion || 0,
          task1_lr: backendMarks.task1_scores?.lexical_resource || 0,
          task1_gra: backendMarks.task1_scores?.grammar_accuracy || 0,
          task2_ta: backendMarks.task2_scores?.task_achievement || 0,
          task2_cc: backendMarks.task2_scores?.coherence_cohesion || 0,
          task2_lr: backendMarks.task2_scores?.lexical_resource || 0,
          task2_gra: backendMarks.task2_scores?.grammar_accuracy || 0,
          overall_summary: backendMarks.instructor_summary || '',
          summary_task1: backendMarks.instructor_summary_task1 || '',
          summary_task2: backendMarks.instructor_summary_task2 || ''
        };
        setMarks(transformedMarks);
        
        // Load task-specific voice notes
        if (backendMarks.voice_note_task1) {
          setVoiceNoteTask1(backendMarks.voice_note_task1);
          setAudioURLTask1(backendMarks.voice_note_task1);
        }
        if (backendMarks.voice_note_task2) {
          setVoiceNoteTask2(backendMarks.voice_note_task2);
          setAudioURLTask2(backendMarks.voice_note_task2);
        }
        // Legacy voice note (for backward compatibility)
        if (backendMarks.voice_note && !backendMarks.voice_note_task1 && !backendMarks.voice_note_task2) {
          setVoiceNote(backendMarks.voice_note);
          setAudioURL(backendMarks.voice_note);
        }
        
        // If published, disable edit mode by default
        if (response.data.status === 'published') {
          setIsEditMode(false);
        } else {
          // If not published, enable edit mode
          setIsEditMode(true);
        }
      } else {
        // No marks exist, enable edit mode
        setIsEditMode(true);
      }
      
      // Load existing comments and convert to frontend format
      if (response.data.comments && response.data.comments.length > 0) {
        const convertedComments = response.data.comments.map(c => {
          // Extract the actual text from answer based on position
          const answer = c.task_num === 1 ? response.data.task1_answer : response.data.task2_answer;
          const extractedText = answer ? answer.substring(c.highlight_start, c.highlight_end) : '';
          
          return {
            id: c.id,
            text: extractedText,
            feedback: c.comment_text,
            task: c.task_num,
            start: c.highlight_start,
            end: c.highlight_end,
            timestamp: c.created_at
          };
        });
        setInlineComments(convertedComments);
      }
      
      // Mark as saved initially
      setAutoSaveStatus('saved');
      setLastAutoSave(new Date());
    } catch (error) {
      toast.error('Failed to load submission');
      navigate('/instructor');
    } finally {
      setLoading(false);
    }
  };

  // Auto-save function - silently saves marks and comments
  const performAutoSave = useCallback(async () => {
    if (!submission || !hasUnsavedChanges.current || !isEditMode) return;
    
    try {
      setAutoSaveStatus('saving');
      
      // Calculate band scores
      const task1Band = (parseFloat(marks.task1_ta) + parseFloat(marks.task1_cc) + 
                         parseFloat(marks.task1_lr) + parseFloat(marks.task1_gra)) / 4 || 0;
      const task2Band = (parseFloat(marks.task2_ta) + parseFloat(marks.task2_cc) + 
                         parseFloat(marks.task2_lr) + parseFloat(marks.task2_gra)) / 4 || 0;
      
      const markData = {
        task1_scores: {
          task_achievement: parseFloat(marks.task1_ta) || 0,
          coherence_cohesion: parseFloat(marks.task1_cc) || 0,
          lexical_resource: parseFloat(marks.task1_lr) || 0,
          grammar_accuracy: parseFloat(marks.task1_gra) || 0,
          band_score: Math.round(task1Band * 2) / 2 // Round to nearest 0.5
        },
        task2_scores: {
          task_achievement: parseFloat(marks.task2_ta) || 0,
          coherence_cohesion: parseFloat(marks.task2_cc) || 0,
          lexical_resource: parseFloat(marks.task2_lr) || 0,
          grammar_accuracy: parseFloat(marks.task2_gra) || 0,
          band_score: Math.round(task2Band * 2) / 2 // Round to nearest 0.5
        },
        instructor_summary: marks.overall_summary || ''
      };
      
      // Save marks
      await axios.post(`/instructor/submissions/${submissionId}/mark`, markData);
      
      // Delete existing comments and save new ones
      await axios.delete(`/instructor/submissions/${submissionId}/comments`);
      
      for (const comment of inlineComments) {
        await axios.post(`/instructor/submissions/${submissionId}/comments`, {
          task_num: comment.task,
          highlight_start: comment.start,
          highlight_end: comment.end,
          comment_text: comment.feedback
        });
      }
      
      hasUnsavedChanges.current = false;
      setAutoSaveStatus('saved');
      setLastAutoSave(new Date());
      
    } catch (error) {
      console.error('Auto-save failed:', error);
      setAutoSaveStatus('error');
    }
  }, [submission, marks, inlineComments, submissionId, isEditMode]);

  // Trigger auto-save when marks or comments change (with debounce)
  const triggerAutoSave = useCallback(() => {
    hasUnsavedChanges.current = true;
    setAutoSaveStatus('unsaved');
    
    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    // Set new timeout for auto-save (2 seconds after last change)
    autoSaveTimeoutRef.current = setTimeout(() => {
      performAutoSave();
    }, 2000);
  }, [performAutoSave]);

  // Watch for marks changes
  useEffect(() => {
    if (submission && isEditMode) {
      triggerAutoSave();
    }
  }, [marks.task1_ta, marks.task1_cc, marks.task1_lr, marks.task1_gra,
      marks.task2_ta, marks.task2_cc, marks.task2_lr, marks.task2_gra,
      marks.overall_summary]);

  // Watch for inline comments changes
  useEffect(() => {
    if (submission && isEditMode && inlineComments.length >= 0) {
      triggerAutoSave();
    }
  }, [inlineComments]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  // Save before leaving page
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges.current) {
        performAutoSave();
        e.preventDefault();
        e.returnValue = '';
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [performAutoSave]);

  const handleTextSelection = () => {
    const selection = window.getSelection();
    const container = answerRef.current;
    if (!selection || selection.rangeCount === 0 || !container) return;

    const rawText = selection.toString();
    if (!rawText.trim()) return;

    const range = selection.getRangeAt(0);
    if (!container.contains(range.startContainer) || !container.contains(range.endContainer)) return;

    const rect = range.getBoundingClientRect();
    const originalAnswer = activeTask === 1 ? task1Answer : task2Answer;

    // Exact offset of (node, offset) inside the container's plain text.
    // Works for text nodes AND element nodes (selection starting/ending on a highlight span boundary).
    const offsetInContainer = (node, offset) => {
      const probe = document.createRange();
      probe.setStart(container, 0);
      probe.setEnd(node, offset);
      return probe.toString().length;
    };

    // Rendered text content is identical to the original answer (highlight spans only wrap it),
    // so DOM offsets map 1:1 to original answer indices.
    let start = offsetInContainer(range.startContainer, range.startOffset);
    let end = start + rawText.length;

    // Trim whitespace edges of the selection (browsers often include trailing spaces/newlines)
    const leading = rawText.length - rawText.replace(/^\s+/, '').length;
    const trailing = rawText.length - rawText.replace(/\s+$/, '').length;
    start += leading;
    end -= trailing;

    const text = rawText.trim();

    // Verify; if the DOM/text mapping drifted, locate the occurrence nearest to the computed offset
    if (originalAnswer.substring(start, end) !== text) {
      const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const tolerant = new RegExp(text.split(/\s+/).map(escapeRe).join('\\s+'), 'g');
      let best = null;
      let match;
      while ((match = tolerant.exec(originalAnswer)) !== null) {
        if (!best || Math.abs(match.index - start) < Math.abs(best.index - start)) {
          best = { index: match.index, length: match[0].length };
        }
        if (match.index === tolerant.lastIndex) tolerant.lastIndex++;
      }
      if (!best) {
        console.error('Could not map selection to answer text', { text, start, end });
        toast.error('Could not locate text position. Please try selecting again.');
        return;
      }
      start = best.index;
      end = best.index + best.length;
    }

    setSelectedText({
      text: originalAnswer.substring(start, end),
      start,
      end,
      task: activeTask
    });

    setFeedbackPopup({
      show: true,
      x: rect.left + window.scrollX,
      y: rect.bottom + window.scrollY + 5
    });
    setFeedbackText('');
  };

  const saveFeedback = () => {
    if (!feedbackText.trim() || !selectedText) return;
    
    const newComment = {
      id: Date.now().toString(),
      text: selectedText.text,
      feedback: feedbackText,
      task: activeTask,
      start: selectedText.start,
      end: selectedText.end,
      timestamp: new Date().toISOString()
    };
    
    setInlineComments(prev => [...prev, newComment]);
    setFeedbackPopup({ show: false, x: 0, y: 0 });
    setSelectedText(null);
    setFeedbackText('');
    
    // Clear selection
    window.getSelection().removeAllRanges();
    
    toast.success('Feedback added');
  };

  const removeFeedback = (commentId) => {
    setInlineComments(prev => prev.filter(c => c.id !== commentId));
    toast.success('Feedback removed');
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(audioBlob);
        
        // Convert to base64 for storage
        const reader = new FileReader();
        reader.readAsDataURL(audioBlob);
        reader.onloadend = () => {
          // Set task-specific voice note based on activeTask
          if (activeTask === 1) {
            setVoiceNoteTask1(reader.result);
            setAudioURLTask1(url);
          } else {
            setVoiceNoteTask2(reader.result);
            setAudioURLTask2(url);
          }
        };

        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      toast.success(`Recording started for Task ${activeTask}...`);
    } catch (error) {
      toast.error('Failed to access microphone');
      console.error('Microphone error:', error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      toast.success('Recording stopped');
    }
  };

  const deleteVoiceNote = () => {
    // Delete task-specific voice note based on activeTask
    if (activeTask === 1) {
      setVoiceNoteTask1(null);
      setAudioURLTask1(null);
    } else {
      setVoiceNoteTask2(null);
      setAudioURLTask2(null);
    }
    toast.success(`Voice note for Task ${activeTask} deleted`);
  };

  const escapeHtml = (s) => s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  const renderAnswerWithHighlights = (answer, task) => {
    if (!answer) return '';
    
    const taskComments = inlineComments.filter(c => c.task === task);
    if (taskComments.length === 0) return escapeHtml(answer);
    
    // Remove duplicate comments (same start and end position)
    const uniqueComments = [];
    const seenPositions = new Set();
    taskComments.forEach(comment => {
      const posKey = `${comment.start}-${comment.end}`;
      if (!seenPositions.has(posKey)) {
        seenPositions.add(posKey);
        uniqueComments.push(comment);
      }
    });
    
    // Create a map of character positions to their covering highlights
    const charHighlights = new Array(answer.length).fill(null).map(() => []);
    
    uniqueComments.forEach(comment => {
      const start = Math.max(0, comment.start);
      const end = Math.min(answer.length, comment.end);
      for (let i = start; i < end; i++) {
        charHighlights[i].push(comment);
      }
    });
    
    // Build result by grouping consecutive characters with same highlight combination
    let result = '';
    let i = 0;
    
    while (i < answer.length) {
      const currentHighlights = charHighlights[i];
      
      if (currentHighlights.length === 0) {
        // No highlight - find the end of unhighlighted text
        let j = i;
        while (j < answer.length && charHighlights[j].length === 0) {
          j++;
        }
        result += escapeHtml(answer.substring(i, j));
        i = j;
      } else {
        // Has highlights - find end of this highlight group
        const currentIds = currentHighlights.map(h => h.id).sort().join(',');
        let j = i;
        while (j < answer.length) {
          const nextHighlights = charHighlights[j];
          const nextIds = nextHighlights.map(h => h.id).sort().join(',');
          if (nextIds !== currentIds) break;
          j++;
        }
        
        const text = answer.substring(i, j);
        
        // Check for both word-level and line-level highlights
        let hasWordLevel = false;
        let hasLineLevel = false;
        let wordLevelComment = null;
        let lineLevelComment = null;
        
        currentHighlights.forEach(h => {
          const hText = answer.substring(h.start, h.end);
          const hWordCount = hText.trim().split(/\s+/).filter(w => w.length > 0).length;
          if (hWordCount <= 3) {
            hasWordLevel = true;
            wordLevelComment = h;
          } else {
            hasLineLevel = true;
            lineLevelComment = h;
          }
        });
        
        let style, className;
        const primaryComment = wordLevelComment || lineLevelComment;
        
        if (hasWordLevel && hasLineLevel) {
          // BOTH: Yellow background + Circle + red underline
          style = `
            background-color: #fef9c3;
            border: 1.5px solid #dc2626;
            border-radius: 50%;
            padding: 0px 3px;
            box-shadow: 0 2px 0 0 #dc2626;
            cursor: pointer;
            position: relative;
            display: inline;
          `.replace(/\s+/g, ' ').trim();
          className = 'inline-comment-highlight word-level-highlight has-parent';
        } else if (hasWordLevel) {
          // Word-level only: Circle + red underline
          style = `
            border: 1.5px solid #dc2626;
            border-radius: 50%;
            padding: 0px 3px;
            box-shadow: 0 2px 0 0 #dc2626;
            cursor: pointer;
            position: relative;
            display: inline;
          `.replace(/\s+/g, ' ').trim();
          className = 'inline-comment-highlight word-level-highlight';
        } else {
          // Line-level only: Yellow background + red underline
          style = `
            background-color: #fef9c3;
            border-bottom: 2px solid #dc2626;
            cursor: pointer;
            position: relative;
          `.replace(/\s+/g, ' ').trim();
          className = 'inline-comment-highlight line-level-highlight';
        }
        
        // Determine highlight type for data attribute
        const highlightType = (hasWordLevel && hasLineLevel) ? 'both' : (hasWordLevel ? 'word' : 'line');
        
        // Get the original text based on primaryComment (which is wordLevel for nested, or the only one available)
        const originalText = answer.substring(primaryComment.start, primaryComment.end);
        
        // Also store line-level text separately if it exists (for showing parent context)
        const lineText = lineLevelComment ? answer.substring(lineLevelComment.start, lineLevelComment.end) : '';
        
        const highlightedSpan = `<span class="${className}" data-comment-id="${primaryComment.id}" data-highlight-type="${highlightType}" data-feedback="${primaryComment.feedback.replace(/"/g, '&quot;')}" data-original-text="${originalText.replace(/"/g, '&quot;')}" data-line-text="${lineText.replace(/"/g, '&quot;')}" style="${style}" title="${primaryComment.feedback.replace(/"/g, '&quot;')}">${escapeHtml(text)}</span>`;
        
        result += highlightedSpan;
        i = j;
      }
    }
    
    return result;
  };

  const handleSaveMarks = async () => {
    setSaving(true);
    try {
      // Transform marks to backend expected format
      const task1Band = parseFloat(calculateTaskBand(
        marks.task1_ta, marks.task1_cc, marks.task1_lr, marks.task1_gra
      ));
      const task2Band = parseFloat(calculateTaskBand(
        marks.task2_ta, marks.task2_cc, marks.task2_lr, marks.task2_gra
      ));
      
      const markData = {
        task1_scores: {
          task_achievement: parseFloat(marks.task1_ta) || 0,
          coherence_cohesion: parseFloat(marks.task1_cc) || 0,
          lexical_resource: parseFloat(marks.task1_lr) || 0,
          grammar_accuracy: parseFloat(marks.task1_gra) || 0,
          band_score: task1Band
        },
        task2_scores: {
          task_achievement: parseFloat(marks.task2_ta) || 0,
          coherence_cohesion: parseFloat(marks.task2_cc) || 0,
          lexical_resource: parseFloat(marks.task2_lr) || 0,
          grammar_accuracy: parseFloat(marks.task2_gra) || 0,
          band_score: task2Band
        },
        instructor_summary: marks.overall_summary || '',  // Legacy field
        instructor_summary_task1: marks.summary_task1 || '',
        instructor_summary_task2: marks.summary_task2 || '',
        voice_note: voiceNote,  // Legacy field
        voice_note_task1: voiceNoteTask1,
        voice_note_task2: voiceNoteTask2
      };
      
      await axios.post(`/instructor/submissions/${submissionId}/mark`, markData);
      
      // Delete existing comments first to prevent duplicates
      await axios.delete(`/instructor/submissions/${submissionId}/comments`);
      
      // Save inline comments
      for (const comment of inlineComments) {
        await axios.post(`/instructor/submissions/${submissionId}/comments`, {
          task_num: comment.task,
          highlight_start: comment.start,
          highlight_end: comment.end,
          comment_text: comment.feedback
        });
      }
      
      toast.success('Marks saved successfully');
    } catch (error) {
      toast.error('Failed to save marks');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!window.confirm('Are you sure you want to publish results? Student will receive notification.')) {
      return;
    }
    
    setSaving(true);
    try {
      // First save marks
      await handleSaveMarks();
      
      // Then publish
      const response = await axios.post(`/instructor/submissions/${submissionId}/publish`);
      
      // Show success message
      toast.success('Results published! Student will receive email notification.', { duration: 5000 });
      
      // Show feedback link if available
      if (response.data.feedback_link) {
        console.log('Feedback link:', response.data.feedback_link);
      }
      
      // Navigate back after a short delay
      setTimeout(() => navigate('/instructor'), 1500);
    } catch (error) {
      console.error('Publish error:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || 'Failed to publish results';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  // IELTS Band Score Rounding: Always rounds to nearest 0.5
  // Examples: 6.25 → 6.0, 6.375 → 6.5, 6.625 → 6.5, 6.75 → 7.0
  const roundToIELTSBand = (score) => {
    if (score === 0) return 0;
    
    const decimal = score - Math.floor(score);
    
    if (decimal < 0.25) {
      // 6.0 to 6.24 → 6.0
      return Math.floor(score);
    } else if (decimal < 0.75) {
      // 6.25 to 6.74 → 6.5
      return Math.floor(score) + 0.5;
    } else {
      // 6.75 to 6.99 → 7.0
      return Math.ceil(score);
    }
  };

  const calculateTaskBand = (ta, cc, lr, gra) => {
    // Ensure all values are numbers, default to 0 if undefined
    const taVal = parseFloat(ta) || 0;
    const ccVal = parseFloat(cc) || 0;
    const lrVal = parseFloat(lr) || 0;
    const graVal = parseFloat(gra) || 0;
    const average = (taVal + ccVal + lrVal + graVal) / 4;
    
    // Round to IELTS standard (nearest 0.5)
    return roundToIELTSBand(average).toFixed(1);
  };

  const calculateOverallBand = () => {
    const taskConfig = submission?.test_details?.task_configuration || 'full';
    
    // For Task 1 Only
    if (taskConfig === 'task_1_only') {
      const task1Band = parseFloat(calculateTaskBand(
        marks.task1_ta, marks.task1_cc, marks.task1_lr, marks.task1_gra
      ));
      return isNaN(task1Band) ? '0.0' : task1Band.toFixed(1);
    }
    
    // For Task 2 Only
    if (taskConfig === 'task_2_only') {
      const task2Band = parseFloat(calculateTaskBand(
        marks.task2_ta, marks.task2_cc, marks.task2_lr, marks.task2_gra
      ));
      return isNaN(task2Band) ? '0.0' : task2Band.toFixed(1);
    }
    
    // For Full Test (both tasks)
    const task1Band = parseFloat(calculateTaskBand(
      marks.task1_ta, marks.task1_cc, marks.task1_lr, marks.task1_gra
    ));
    const task2Band = parseFloat(calculateTaskBand(
      marks.task2_ta, marks.task2_cc, marks.task2_lr, marks.task2_gra
    ));
    
    // Check if both bands are valid numbers
    if (isNaN(task1Band) || isNaN(task2Band)) {
      return '0.0';
    }
    
    // IELTS Overall Band = (Task1 + Task2 × 2) / 3
    const overallAverage = (task1Band + task2Band * 2) / 3;
    
    // Round to IELTS standard (nearest 0.5)
    return roundToIELTSBand(overallAverage).toFixed(1);
  };

  const showQuestionPreview = (task) => {
    setCurrentQuestion({
      task: task,
      prompt: task === 1 ? submission.test_details.task1_prompt : submission.test_details.task2_prompt,
      image: task === 1 ? submission.test_details.task1_image : null
    });
    setShowQuestionModal(true);
  };

  const renderFormattedText = (text) => {
    if (!text) return '';
    let formatted = text
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/__(.+?)__/g, '<u>$1</u>')
      .replace(/\n/g, '<br/>');
    return formatted;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-main-gradient">
        <div className="text-slate-600">Loading submission...</div>
      </div>
    );
  }

  if (!submission) {
    return null;
  }

  const task1Answer = submission.task1_answer || '';
  const task2Answer = submission.task2_answer || '';

  return (
    <div className="min-h-screen bg-main-gradient">
      {/* Header */}
      <div className="header-gradient">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <Button
                variant="outline"
                onClick={() => navigate('/instructor')}
                className="border-white text-white hover:bg-white hover:text-purple-600 text-xs sm:text-sm"
              >
                <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                <span className="hidden sm:inline">Back</span>
              </Button>
              <div>
                <h1 className="text-base sm:text-xl font-bold text-white">Mark Submission</h1>
                <p className="text-xs sm:text-sm text-white opacity-80 truncate max-w-[200px] sm:max-w-none">
                  {submission.candidate_name} • {submission.candidate_email}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {submission.status === 'published' && !isEditMode && (
                <Button
                  onClick={() => setIsEditMode(true)}
                  variant="outline"
                  className="border-white text-white hover:bg-white hover:text-purple-600 text-xs sm:text-sm"
                >
                  <Edit className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Edit Marks</span>
                  <span className="sm:hidden">Edit</span>
                </Button>
              )}
              {isEditMode && (
                <>
                  {/* Auto-save status indicator */}
                  <div className="flex items-center gap-1 text-xs text-white/80 mr-2">
                    {autoSaveStatus === 'saving' && (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        <span className="hidden sm:inline">Saving...</span>
                      </>
                    )}
                    {autoSaveStatus === 'saved' && (
                      <>
                        <Cloud className="w-3 h-3 text-green-300" />
                        <span className="hidden sm:inline">Saved</span>
                      </>
                    )}
                    {autoSaveStatus === 'unsaved' && (
                      <>
                        <CloudOff className="w-3 h-3 text-yellow-300" />
                        <span className="hidden sm:inline">Unsaved</span>
                      </>
                    )}
                    {autoSaveStatus === 'error' && (
                      <>
                        <CloudOff className="w-3 h-3 text-red-300" />
                        <span className="hidden sm:inline">Error</span>
                      </>
                    )}
                  </div>
                  <Button
                    onClick={handleSaveMarks}
                    disabled={saving}
                    className="text-white text-xs sm:text-sm hover:opacity-90"
                    style={{background: 'linear-gradient(135deg, #E53935 0%, #B71C1C 100%)'}}
                  >
                    <Save className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                    {saving ? 'Saving...' : <><span className="hidden sm:inline">Save Marks</span><span className="sm:hidden">Save</span></>}
                  </Button>
                  <Button
                    onClick={handlePublish}
                    disabled={saving}
                    className="text-white text-xs sm:text-sm hover:opacity-90"
                    style={{background: 'linear-gradient(135deg, #E53935 0%, #B71C1C 100%)'}}
                  >
                    <Send className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">{submission.status === 'published' ? 'Re-publish' : 'Publish Results'}</span>
                    <span className="sm:hidden">Publish</span>
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-2 sm:px-4 py-4 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
          {/* Left: Answer Area */}
          <div className="lg:col-span-2 space-y-4">
            {/* Task Tabs */}
            {/* Task Tabs - Only show if both tasks exist */}
            {(submission.test_details.task_configuration === 'full' || !submission.test_details.task_configuration) && (
              <div className="flex bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                <button
                  onClick={() => setActiveTask(1)}
                  className={`flex-1 py-3 px-6 font-medium transition-colors ${
                    activeTask === 1
                      ? 'bg-purple-600 text-white'
                      : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  Task 1
                </button>
                <button
                  onClick={() => setActiveTask(2)}
                  className={`flex-1 py-3 px-6 font-medium transition-colors ${
                    activeTask === 2
                      ? 'bg-purple-600 text-white'
                      : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  Task 2
                </button>
              </div>
            )}

            {/* Answer Display */}
            <Card className="glass-card">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3">
                  <div className="flex-1">
                    <CardTitle className="text-base sm:text-lg text-slate-700 mb-2">
                      Student Answer - Task {activeTask}
                    </CardTitle>
                    {/* Word Count - Mobile & Desktop */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm">
                      <div className="flex items-center gap-1.5">
                        <span className="text-slate-500">Total:</span>
                        <span className="font-bold text-blue-600 text-sm sm:text-base">
                          {submission.word_count_task1 && submission.word_count_task2 
                            ? submission.word_count_task1 + submission.word_count_task2 
                            : 'N/A'}
                        </span>
                        <span className="text-slate-400">words</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="h-3 w-px bg-slate-300"></div>
                        <span className={`font-semibold ${activeTask === 1 ? 'text-purple-600' : 'text-slate-500'}`}>
                          T1: {submission.word_count_task1 || 0}
                        </span>
                        <span className={`font-semibold ${activeTask === 2 ? 'text-purple-600' : 'text-slate-500'}`}>
                          T2: {submission.word_count_task2 || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => showQuestionPreview(activeTask)}
                    className="text-xs sm:text-sm self-start sm:self-auto"
                  >
                    <Eye className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">View Question</span>
                    <span className="sm:hidden">Question</span>
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-slate-50 p-6 rounded-lg border border-slate-200">
                  <div className="text-xs sm:text-sm text-slate-500 mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <strong className="text-blue-700">Instructions:</strong>{' '}
                    {isEditMode ? (
                      <>
                        <span className="hidden sm:inline">Select any text to add inline feedback</span>
                        <span className="sm:hidden">Tap and hold text, then select to add feedback</span>
                      </>
                    ) : (
                      'Viewing mode - feedback editing disabled'
                    )}
                  </div>
                  <div
                    ref={answerRef}
                    className="text-slate-700 leading-relaxed whitespace-pre-wrap select-text cursor-text"
                    onMouseUp={isEditMode ? handleTextSelection : undefined}
                    onTouchEnd={isEditMode ? handleTextSelection : undefined}
                    onClick={(e) => {
                      const target = e.target;
                      if (target.classList.contains('inline-comment-highlight')) {
                        const feedback = target.getAttribute('data-feedback');
                        // Use data-original-text if available (for nested highlights), otherwise use textContent
                        const originalText = target.getAttribute('data-original-text');
                        const text = originalText || target.textContent;
                        if (feedback) {
                          toast.info(
                            <div>
                              <div className="font-semibold mb-1">"{text}"</div>
                              <div>{feedback}</div>
                            </div>,
                            { duration: 4000 }
                          );
                        }
                      }
                    }}
                    style={{ 
                      WebkitUserSelect: 'text',
                      userSelect: 'text',
                      WebkitTouchCallout: 'default'
                    }}
                    dangerouslySetInnerHTML={{
                      __html: renderAnswerWithHighlights(
                        activeTask === 1 ? task1Answer : task2Answer,
                        activeTask
                      )
                    }}
                  />
                  {(activeTask === 1 ? task1Answer : task2Answer).length === 0 && (
                    <div className="text-center text-slate-400 py-8">
                      No answer submitted for this task
                    </div>
                  )}
                </div>

                {/* Inline Comments List */}
                {inlineComments.filter(c => c.task === activeTask).length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3">
                      <MessageSquare className="w-4 h-4 inline mr-2" />
                      Inline Feedback ({inlineComments.filter(c => c.task === activeTask).length})
                    </h4>
                    <div className="space-y-2">
                      {inlineComments.filter(c => c.task === activeTask).map(comment => (
                        <div
                          key={comment.id}
                          className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="font-medium text-slate-700 mb-1">
                                "{comment.text}"
                              </div>
                              <div className="text-slate-600">{comment.feedback}</div>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => removeFeedback(comment.id)}
                              className="text-red-500 hover:text-red-700"
                              disabled={!isEditMode}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right: Marking Panel */}
          <div className="space-y-4">
            {/* Task Scores */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-base sm:text-lg text-slate-700">
                  Task {activeTask} Scores
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {(() => {
                  // Different criteria for Task 1 and Task 2
                  const criteriaList = activeTask === 1 
                    ? ['Task Achievement', 'Coherence & Cohesion', 'Lexical Resource', 'Grammatical Range & Accuracy']
                    : ['Task Response', 'Coherence & Cohesion', 'Lexical Resource', 'Grammatical Range & Accuracy'];
                  
                  const keys = ['ta', 'cc', 'lr', 'gra'];
                  
                  // Band scores from 0 to 9 (whole numbers only)
                  const bandScores = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
                  
                  return criteriaList.map((criteria, idx) => {
                    const key = `task${activeTask}_${keys[idx]}`;
                    const currentValue = marks[key] !== undefined ? marks[key].toString() : '0';
                    return (
                      <div key={criteria}>
                        <Label className="text-sm text-slate-700">{criteria}</Label>
                        <Select
                          value={currentValue}
                          onValueChange={(value) => setMarks({ ...marks, [key]: parseFloat(value) })}
                          disabled={!isEditMode}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select band" />
                          </SelectTrigger>
                          <SelectContent>
                            {bandScores.map((score) => (
                              <SelectItem key={score} value={score}>
                                Band {score}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  });
                })()}
                <div className="bg-green-50 rounded-lg p-3 sm:p-4 text-center">
                  <div className="text-xs sm:text-sm font-medium text-slate-700">Task {activeTask} Band</div>
                  <div className="text-2xl sm:text-3xl font-bold" style={{background: 'linear-gradient(135deg, #2E7D32 0%, #00C853 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text'}}>
                    {calculateTaskBand(
                      marks[`task${activeTask}_ta`],
                      marks[`task${activeTask}_cc`],
                      marks[`task${activeTask}_lr`],
                      marks[`task${activeTask}_gra`]
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Overall Band */}
            <Card className="glass-card bg-gradient-to-br from-red-50 to-pink-50">
              <CardContent className="py-4 sm:py-6 text-center">
                <div className="text-xs sm:text-sm font-medium text-slate-700 mb-2">Overall Band Score</div>
                <div className="text-3xl sm:text-5xl font-bold" style={{background: 'linear-gradient(135deg, #2E7D32 0%, #00C853 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text'}}>
                  {calculateOverallBand()}
                </div>
                <div className="text-[10px] sm:text-xs text-slate-500 mt-2">
                  {(() => {
                    const taskConfig = submission?.test_details?.task_configuration || 'full';
                    if (taskConfig === 'task_1_only') return 'Task 1 Only';
                    if (taskConfig === 'task_2_only') return 'Task 2 Only';
                    return 'Formula: (Task1 + Task2 × 2) ÷ 3';
                  })()}
                </div>
              </CardContent>
            </Card>

            {/* Instructor's Suggestion - Task Specific */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-sm sm:text-base text-slate-700">
                  Instructor's Summary (Task {activeTask})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder={`Write feedback for Task ${activeTask}...`}
                  value={activeTask === 1 ? marks.summary_task1 : marks.summary_task2}
                  onChange={(e) => {
                    if (activeTask === 1) {
                      setMarks({ ...marks, summary_task1: e.target.value });
                    } else {
                      setMarks({ ...marks, summary_task2: e.target.value });
                    }
                  }}
                  rows={6}
                  className="resize-none"
                  disabled={!isEditMode}
                />
              </CardContent>
            </Card>

            {/* Voice Note - Task Specific */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-sm sm:text-base text-slate-700 flex items-center">
                  <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                  Voice Feedback (Task {activeTask})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!(activeTask === 1 ? voiceNoteTask1 : voiceNoteTask2) ? (
                  <div className="space-y-3">
                    {!isRecording ? (
                      <Button
                        onClick={startRecording}
                        className="w-full bg-red-600 hover:bg-red-700 text-white text-xs sm:text-sm"
                        disabled={!isEditMode}
                      >
                        <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                        Start Recording
                      </Button>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex items-center justify-center space-x-2 p-3 sm:p-4 bg-red-50 border-2 border-red-500 rounded-lg animate-pulse">
                          <div className="w-2 h-2 sm:w-3 sm:h-3 bg-red-600 rounded-full"></div>
                          <span className="text-xs sm:text-sm text-red-600 font-semibold">Recording...</span>
                        </div>
                        <Button
                          onClick={stopRecording}
                          className="w-full bg-slate-600 hover:bg-slate-700 text-white text-xs sm:text-sm"
                        >
                          Stop Recording
                        </Button>
                      </div>
                    )}
                    <p className="text-[10px] sm:text-xs text-slate-500 text-center">
                      Record audio feedback for Task {activeTask}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-green-700">Voice Note Recorded</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={deleteVoiceNote}
                          className="text-red-500 hover:text-red-700"
                          disabled={!isEditMode}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      {(activeTask === 1 ? audioURLTask1 : audioURLTask2) && (
                        <audio controls className="w-full">
                          <source src={activeTask === 1 ? audioURLTask1 : audioURLTask2} type="audio/webm" />
                          Your browser does not support audio playback.
                        </audio>
                      )}
                    </div>
                    <Button
                      onClick={startRecording}
                      variant="outline"
                      className="w-full"
                      disabled={!isEditMode}
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Re-record
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Question Preview Modal */}
      <Dialog open={showQuestionModal} onOpenChange={setShowQuestionModal}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-slate-700">Task {currentQuestion?.task} Question</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div 
              className="text-slate-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: renderFormattedText(currentQuestion?.prompt) }}
            />
            {currentQuestion?.image && (
              <div className="border rounded-lg p-4 bg-slate-50">
                <img
                  src={currentQuestion.image}
                  alt="Task visual"
                  className="max-w-full mx-auto"
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Feedback Popup - Responsive */}
      {feedbackPopup.show && (
        <div
          className="fixed z-50 bg-white rounded-lg shadow-2xl border-2 border-purple-500 p-3 sm:p-4 max-w-[90vw] sm:max-w-[320px]"
          style={{
            left: window.innerWidth < 640 
              ? '50%' 
              : `${Math.min(feedbackPopup.x, window.innerWidth - 340)}px`,
            top: window.innerWidth < 640
              ? '50%'
              : `${Math.min(feedbackPopup.y, window.innerHeight - 250)}px`,
            transform: window.innerWidth < 640 ? 'translate(-50%, -50%)' : 'none',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs sm:text-sm font-semibold text-slate-700">Add Feedback</div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setFeedbackPopup({ show: false, x: 0, y: 0 })}
              className="h-8 w-8 p-0"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded p-2 mb-3 text-[10px] sm:text-xs text-slate-600 max-h-20 overflow-y-auto">
            <strong>Selected:</strong> "{selectedText?.text?.substring(0, 100)}{selectedText?.text?.length > 100 ? '...' : ''}"
          </div>
          <Textarea
            placeholder="Enter your feedback..."
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            rows={3}
            className="mb-3 text-xs sm:text-sm"
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setFeedbackPopup({ show: false, x: 0, y: 0 })}
              className="text-xs"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="bg-purple-600 hover:bg-purple-700 text-white text-xs"
              onClick={saveFeedback}
              disabled={!feedbackText.trim()}
              data-testid="inline-feedback-save-btn"
            >
              <Save className="w-3 h-3 mr-1" />
              Save
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubmissionMarkingPage;
